import { useCallback, useState } from "react";
import { IBlog } from "../../types/CommonTypes";
import {
	collection,
	getDocs,
	getFirestore,
	limit,
	orderBy,
	query,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { calculateSlug, getRandomId } from "../constants/helpers";
import { toast } from "react-toastify";
import useFirebase from "./useFirebase";

interface IProps {
	resetBlog?: any;
}

const useBlog = ({ resetBlog }: IProps) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [blogs, setBlogs] = useState<IBlog[]>([]);
	const { deleteDocumentById, createDocument } = useFirebase();

	const handleCreateBlog = async (blog: IBlog) => {
		const body = {
			...blog,
			id: blog.id ?? getRandomId(),
			isActive: true,
			createdAt: new Date().toISOString(),
			updatedAt: new Date().toISOString(),
			slug: blog.slug || calculateSlug(blog.title || ""),
		};

		try {
			setLoading(true);
			//Primero cargamos la imagen
			const resultUpload = await handleUploadImage(
				body.id!,
				getRandomId(),
				blog.picture
			);
			if (resultUpload) {
				await createDocument(
					{
						...body,
						picture: resultUpload,
					},
					"blogs/",
					(res) => {
						if (res) {
							resetBlog();
							toast.success(
								"El blog ha sido guardado correctamente"
							);
						} else {
							toast.error("No pudimos guardar el blog");
						}
						setLoading(false);
					}
				);
			} else {
				console.log("Error uploading image");
				toast.error("Error guardando la imagen");
			}
		} catch (error) {
			console.error("Error creating blog", error, body);
			toast.error("Error creando blog");
		} finally {
			setLoading(false);
		}
	};

	const handleUploadImage = async (
		refId: string,
		fileName: string,
		file: File
	) => {
		setLoading(true);
		try {
			const storage = getStorage();
			const storageRef = ref(
				storage,
				`blogs/${refId}/${fileName}.${file.type.split("/")[1]}`
			);
			const snapshot = await uploadBytes(storageRef, file);
			const downloadURL = await getDownloadURL(snapshot.ref);
			if (downloadURL) {
				console.log("Uploaded a blob or file!", snapshot);
				return {
					name: fileName,
					url: downloadURL,
					path: snapshot.ref.fullPath,
				};
			}
			return null;
		} catch (error) {
			console.error("Error uploading image", error);
			return null;
		} finally {
			setLoading(false);
		}
	};

	const retrieveBlogs = useCallback(async (limitCount: number) => {
		setLoading(true);
		try {
			const db = getFirestore();
			const blogsCollection = collection(db, "blogs");
			const blogsQuery = query(
				blogsCollection,
				orderBy("date", "desc"),
				limit(limitCount)
			);
			const blogsSnapshot = await getDocs(blogsQuery);
			const blogsList = blogsSnapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
			}));
			// El orden lo define el query (orderBy "date" desc). Reordenar aquí
			// por otro campo sólo barajaría la ventana que el servidor eligió.
			setBlogs(blogsList);
		} catch (error) {
			console.error("Error retrieving blogs", error);
		} finally {
			setLoading(false);
		}
	}, []);

	const deleteBlog = async (blog: IBlog) => {
		setLoading(true);
		await deleteDocumentById(
			"blogs/",
			blog.id ?? "",
			(success: boolean) => {
				if (success) {
					setBlogs(blogs.filter((item) => item.id !== blog.id));
					toast.success("El elemento ha sido eliminado");
				} else {
					toast.error("No se ha podido eliminar el elemento");
				}
				setLoading(false);
			},
			blog.picture?.path ?? null
		);
	};

	return {
		loading,
		blogs,
		retrieveBlogs,
		handleCreateBlog,
		deleteBlog,
		// changeStatusBlog,
	};
};

export default useBlog;
